/**
 * EscrowPaymentModal - First-time escrow explainer modal
 *
 * Shown once per user (controlled by localStorage) to explain how the
 * escrow payment system works before they proceed to fund a project.
 *
 * Features:
 *   - 4-step vertical timeline layout (Figma 1067:11386)
 *   - Left green circle icon with connector lines between steps
 *   - Right card with left green border accent for each step
 *   - "Don't show again" checkbox persisted in localStorage
 *   - Escape key + overlay click dismissal
 *   - Focus trap for accessibility
 */

import * as React from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import { cn } from '@lib/cn';
import { Button, CustomCheckbox } from '@components/ui';

const storageKey = () =>
  'escrow-modal-dismissed:' +
  (useAuthStore.getState().session?.principalId ?? 'anonymous');

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EscrowPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional: navigate to payment page after the user clicks "Understood". */
  onProceedToPayment?: () => void;
}

interface EscrowStep {
  icon: string;
  title: string;
  description: string;
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

function getEscrowSteps(currencySymbol: string): EscrowStep[] {
  return [
    {
      icon: 'ri-file-list-3-line',
      title: 'You approve execution',
      description:
        `Execution approval uses your available ${currencySymbol} balance. Accepting the planning delivery does not transfer funds.`,
    },
    {
      icon: 'ri-lock-line',
      title: 'Tokens are locked in escrow',
      description:
        `The execution budget is reserved in ${currencySymbol} escrow when you approve the proposal. Opening or dismissing this explanation does not move funds.`,
    },
    {
      icon: 'ri-checkbox-circle-line',
      title: 'You approve the result',
      description:
        "Once you're happy with the delivery of the milestones, you release the payment from escrow. Only then do the consultant and the team get paid.",
    },
    {
      icon: 'ri-shield-check-line',
      title: "You're protected",
      description:
        'An open dispute freezes the affected funds. Dispute resolution is not currently available in this backend.',
    },
  ];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns true when the user previously dismissed this modal. */
export function isEscrowModalDismissed(): boolean {
  try {
    return localStorage.getItem(storageKey()) === 'true';
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EscrowPaymentModal({
  isOpen,
  onClose,
  onProceedToPayment,
}: EscrowPaymentModalProps) {
  const { currency } = useDaoConfig();
  const steps = React.useMemo(() => getEscrowSteps(currency.symbol), [currency.symbol]);
  const [dontShowAgain, setDontShowAgain] = React.useState(false);
  const modalRef = React.useRef<HTMLDivElement>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);

  // --------------------------------------------------------------------------
  // Keyboard handling + focus trap
  // --------------------------------------------------------------------------

  React.useEffect(() => {
    if (!isOpen) return;

    const previousFocus = document.activeElement as HTMLElement | null;
    // Move focus into the modal when it opens
    closeButtonRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        handleClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const modal = modalRef.current;
      if (!modal) return;

      const focusableSelectors =
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const focusableElements = Array.from(
        modal.querySelectorAll<HTMLElement>(focusableSelectors)
      );

      if (focusableElements.length === 0) return;

      const first = focusableElements[0] as HTMLElement;
      const last = focusableElements[
        focusableElements.length - 1
      ] as HTMLElement;

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Prevent body scroll while modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // --------------------------------------------------------------------------
  // Handlers
  // --------------------------------------------------------------------------

  function handleClose() {
    if (dontShowAgain) {
      try {
        localStorage.setItem(storageKey(), 'true');
      } catch {
        // localStorage may be unavailable in some environments
      }
    }
    onClose();
  }

  function handleUnderstood() {
    handleClose();
    onProceedToPayment?.();
  }

  function handleOverlayClick(event: React.MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  }

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  if (!isOpen) return null;

  return (
    <div
      className="modalWindowOverlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="escrow-modal-title"
    >
      <div
        ref={modalRef}
        className={cn(
          'modalWindow flex max-h-[95dvh] w-fit max-w-[778px] flex-col gap-[--spacing-12] p-[--spacing-12] pt-[--spacing-10]'
        )}
      >
        {/* Header */}
        <div>
          <h2 id="escrow-modal-title" className="text-2xl font-semibold">
            Escrow Payment
          </h2>
          <p className="max-w-[70ch] text-balance text-base">
            Lock your funds in order for the project coordinator to be able to
            set up the team and start working on the project. We use escrow to
            protect both you and the development team.
          </p>
        </div>

        {/* Vertical timeline */}
        <div className="flex h-full flex-col overflow-y-scroll py-[--spacing-16]">
          {steps.map((step, index) => {
            const isLast = index === steps.length - 1;
            return (
              <div key={step.title} className="flex gap-4">
                {/* Left column: icon circle + connector line */}
                <div className="relative flex flex-col items-center pt-[24px]">
                  {/* Icon circle */}
                  <div
                    className={cn(
                      'z-10 flex aspect-square h-[40px] shrink-0 items-center justify-center rounded-full bg-[--colors-emerald-200]'
                    )}
                  >
                    <i
                      className={cn(step.icon, 'text-2xl')}
                      aria-hidden="true"
                    />
                  </div>
                  {/* Connector line (not shown after last step) */}
                  {!isLast && (
                    <div className="absolute left-[20px] top-[32px] z-0 h-full border border-[--state-brand-selected]" />
                  )}
                </div>

                {/* Right column: step card */}
                <div
                  className={cn(
                    'mb-[--spacing-14] flex-1',
                    'py-[--spacing-7] pl-[--spacing-10] pr-[--spacing-12]',
                    'rounded-[8px] border border-[--colors-emerald-500]',
                    isLast && 'mb-0'
                  )}
                >
                  <p className="text-base font-semibold">{step.title}</p>
                  <p className="text-balance text-sm text-[--text-dark-secondary]">
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <CustomCheckbox
          id="escrow-dont-show"
          checked={dontShowAgain}
          onChange={setDontShowAgain}
          label="Don't Show Again"
          size="lg"
        />

        {/* CTA button */}
        <Button
          ref={closeButtonRef}
          variant="primary"
          className="h-[44px] min-h-[44px] w-full"
          onClick={handleUnderstood}
        >
          Understood
        </Button>
      </div>
    </div>
  );
}
